import React, { useEffect, useMemo } from 'react'
import { Map as LeafletMap } from 'leaflet'
import { MapContainer } from 'react-leaflet/MapContainer'
import { useMap } from 'react-leaflet'
import MapTileLayer from '@/javascript/presentation/components/maps/map-tile-layer'
import FullscreenControl from '@/javascript/presentation/components/maps/fullscreen-control'
import { RouteSuggestion } from '@/javascript/domain/entities/route-suggestion'
import { RouteItem } from '@/javascript/domain/entities/maps/route-item'
import { palette } from '@/javascript/presentation/helpers/palette'
import RoutePolyline from '@/javascript/presentation/pages/routing/components/route-polyline'
import RouteMarkers from '@/javascript/presentation/pages/routing/components/route-markers'
import { getBoundsFromPoints } from '@/javascript/presentation/helpers/map-utils'

type RouteMapEntry = { geometry: string; points: { location: { lat: number; lng: number } }[] }

type Props = {
  expandedIndex: number | null
} & (
  { routes: RouteItem[]; routeSuggestions?: never } |
  { routeSuggestions: RouteSuggestion[]; routes?: never }
)

function FitBoundsEffect({ entries }: { entries: RouteMapEntry[] }) {
  const map = useMap()
  useEffect(() => {
    const points = entries.flatMap(e => e.points.map(p => [p.location.lat, p.location.lng] as [number, number]))
    const bounds = getBoundsFromPoints(points)
    if (bounds) map.fitBounds(bounds, { padding: [40, 40] })
  }, [entries, map])
  return null
}

const RouteMapView = React.forwardRef<LeafletMap, Props>(({ expandedIndex, routes, routeSuggestions }, ref) => {
  const entries: RouteMapEntry[] = useMemo(
    () => routes ? routes.flatMap(r => r.members) : (routeSuggestions ?? []),
    [routes, routeSuggestions]
  )

  return (
    <MapContainer ref={ref} center={[-25.4804975, -49.2740054]} zoom={13} scrollWheelZoom={true}
      style={{ width: '100%', height: '100%' }}
    >
      <MapTileLayer />
      <FullscreenControl />
      <FitBoundsEffect entries={entries} />
      {entries.map((entry, index) => (
        <RoutePolyline
          key={index}
          geometry={entry.geometry}
          color={palette[index % palette.length].color}
          isSelected={expandedIndex === index}
        />
      ))}
      <RouteMarkers groups={entries} />
    </MapContainer>
  )
})

export default RouteMapView
